import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Building2, ShieldCheck, Users, Landmark, Sparkles, Check, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { PILLARS } from "@/lib/data";

const ICONS = { Building2, ShieldCheck, Users, Landmark, Sparkles };

const PageHeader = ({ overline, title, subtitle }) => (
  <section className="bg-navy text-cream pt-40 pb-24 grain">
    <div className="relative mx-auto max-w-7xl px-6">
      <Reveal>
        <p className="overline mb-6">{overline}</p>
        <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-cream">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-8 max-w-2xl text-lg text-cream/75 leading-relaxed">{subtitle}</p>
        )}
      </Reveal>
    </div>
  </section>
);

const Expertise = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <div data-testid="expertise-page">
      <PageHeader
        overline="Our Expertise"
        title="Five strategic pillars of children's services."
        subtitle="Rather than listing many individual services, our work is organised into five focused, specialist pillars — designed to help organisations establish, strengthen and transform children's services."
      />

      {PILLARS.map((p, idx) => {
        const Icon = ICONS[p.icon];
        const reversed = idx % 2 === 1;
        return (
          <section
            key={p.id}
            id={p.id}
            data-testid={`expertise-pillar-${p.id}`}
            className={`scroll-mt-24 ${idx % 2 === 1 ? "bg-white border-y border-slate-200/70" : "bg-cream"}`}
          >
            <div className="mx-auto max-w-7xl px-6 py-24">
              <div className={`grid gap-12 lg:grid-cols-2 lg:items-center`}>
                <Reveal className={reversed ? "lg:order-2" : ""}>
                  <div className="relative overflow-hidden rounded-3xl soft-shadow">
                    <img src={p.image} alt={p.title} className="h-[460px] w-full object-cover" />
                    <div className="absolute left-6 top-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-navy/90 backdrop-blur text-gold">
                      <span className="font-serif text-2xl">{p.number}</span>
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={0.1} className={reversed ? "lg:order-1" : ""}>
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-cream">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight">{p.title}</h2>
                  <p className="mt-4 text-lg text-slate-600 leading-relaxed max-w-lg">{p.summary}</p>
                  <ul className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-navy/85">
                        <Check size={18} className="mt-1 shrink-0 text-gold" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  {p.id === "government-strategic-advisory" && (
                    <p className="mt-8 rounded-2xl border border-gold/40 bg-gold/10 p-5 text-sm text-navy/80 leading-relaxed">
                      We position Epic Consultancy as a trusted partner willing to work alongside
                      governments and organisations — using collaborative language and evidence-based
                      advice.
                    </p>
                  )}
                </Reveal>
              </div>
            </div>
          </section>
        );
      })}

      <section className="mx-auto max-w-7xl px-6 py-28">
        <Reveal className="rounded-[2rem] bg-teal px-8 py-16 md:px-16 text-cream flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-cream">Not sure where to begin?</h2>
            <p className="mt-3 text-cream/80 max-w-xl">
              Book a consultation and we'll help you identify the right starting point for your service.
            </p>
          </div>
          <Button asChild className="rounded-full bg-gold text-navy hover:bg-gold-bright px-8 h-12 shrink-0">
            <Link to="/contact" data-testid="expertise-cta">
              Book a Consultation <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
        </Reveal>
      </section>
    </div>
  );
};

export default Expertise;
